import { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin panel — license actions (toggle/delete/resend/bulk-resend/copy/notif-check).
 * Extracted from AdminPanel.jsx on 29/05/2026 (code review refactor).
 */
export default function useLicenseActions({ admin, fetchLicenses, fetchStats, fetchNotifLog }) {
  const [copied, setCopied] = useState(null);
  const [resendingId, setResendingId] = useState(null);
  const [bulkResendingId, setBulkResendingId] = useState(null);
  const [checkingNotifs, setCheckingNotifs] = useState(false);

  const toggleLicense = async (lic) => {
    await fetch(`${API}/api/licenses/${lic.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${admin.token}` },
      body: JSON.stringify({ is_active: !lic.is_active }),
    });
    fetchLicenses();
    fetchStats();
  };

  const deleteLicense = async (lic) => {
    if (!window.confirm(`Supprimer la licence de ${lic.holder_name} ?`)) return;
    await fetch(`${API}/api/licenses/${lic.id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${admin.token}` },
    });
    fetchLicenses();
    fetchStats();
  };

  const resendWelcome = async (lic) => {
    const recipient = lic.holder_email || lic.contact_email;
    if (!recipient) {
      alert("Aucun email destinataire associé à cette licence (ni email titulaire, ni contact).");
      return;
    }
    const typeLabel = lic.is_master ? 'Responsable Pédagogique' : 'Apprenant';
    if (!window.confirm(`Renvoyer l'email de bienvenue ${typeLabel} à ${recipient} ?`)) return;
    setResendingId(lic.id);
    try {
      const res = await fetch(`${API}/api/licenses/${lic.id}/resend-welcome`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${admin.token}` },
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Erreur');
      }
      const data = await res.json();
      alert(`✅ Email ${typeLabel} renvoyé à ${data.recipient}`);
    } catch (e) {
      alert(`❌ Échec de l'envoi : ${e.message}`);
    }
    setResendingId(null);
  };

  const bulkResendCohort = async (master, children) => {
    const count = children.length;
    if (count === 0) return;
    if (!window.confirm(
      `Renvoyer l'email de bienvenue à TOUS les ${count} apprenant·s de la cohorte « ${master.holder_name} » ?\n\n` +
      `Les apprenants sans adresse email seront ignorés.`,
    )) return;
    setBulkResendingId(master.id);
    try {
      const res = await fetch(`${API}/api/licenses/bulk-resend-welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${admin.token}` },
        body: JSON.stringify({ master_license_id: master.id, include_master: false }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Erreur');
      }
      const data = await res.json();
      const failedList = (data.details || []).filter((d) => d.status === 'failed').slice(0, 5)
        .map((d) => `  • ${d.holder}: ${d.reason}`).join('\n');
      const skippedList = (data.details || []).filter((d) => d.status === 'skipped').slice(0, 5)
        .map((d) => `  • ${d.holder}`).join('\n');
      alert(
        `Résultat — cohorte « ${master.holder_name} »\n\n` +
        `✅ Envoyés : ${data.sent}\n` +
        `⚠️ Ignorés (pas d'email) : ${data.skipped}` + (skippedList ? `\n${skippedList}` : '') + `\n` +
        `❌ Échecs : ${data.failed}` + (failedList ? `\n${failedList}` : ''),
      );
    } catch (e) {
      alert(`❌ Erreur lors de l'envoi en masse : ${e.message}`);
    }
    setBulkResendingId(null);
  };

  const triggerNotifCheck = async () => {
    setCheckingNotifs(true);
    try {
      const res = await fetch(`${API}/api/notifications/check`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${admin.token}` },
      });
      if (res.ok) {
        const data = await res.json();
        alert(`${data.sent} notification(s) envoyée(s)`);
        fetchNotifLog();
      }
    } catch (err) { console.warn('triggerNotifCheck error:', err.message); }
    setCheckingNotifs(false);
  };

  const copyKey = (key) => {
    navigator.clipboard.writeText(key);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  return {
    copied, resendingId, bulkResendingId, checkingNotifs,
    toggleLicense, deleteLicense, resendWelcome, bulkResendCohort,
    triggerNotifCheck, copyKey,
  };
}
